import { useState, type ReactNode } from "react";
import { AlertOctagon, Download, Eye, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DetailHeader } from "@/components/padroes";
import { StatusBadge } from "@/components/StatusBadge";
import { TipoNFBadge } from "@/components/TipoNFBadge";
import type { NotaFiscal } from "@/data/mock";
import { api } from "@/lib/api";
import { triggerDownload } from "@/lib/download";
import { formatBRL, formatCNPJ, formatDateTime, formatMesRef } from "@/lib/format";
import { cn } from "@/lib/utils";

// Peças comuns ao detalhe da NF do RH e do colaborador.

/** Cabeçalho: "← {lista}", "NF nº X" + tipo + status; valor de 28px à direita. */
export const CabecalhoNF = ({ nf, voltar }: { nf: NotaFiscal; voltar: { to: string; label: string } }) => (
  <DetailHeader
    voltar={voltar}
    titulo={`NF nº ${nf.numero}`}
    status={
      <>
        <TipoNFBadge tipo={nf.tipo} sempre />
        <StatusBadge status={nf.status} />
      </>
    }
    subtitulo={`${nf.colaboradorNome} · ${formatCNPJ(nf.cnpj)}`}
    acoes={
      <div className="text-right">
        <div className="text-xs text-muted-foreground">Valor da nota</div>
        <div className="text-[28px] font-semibold tabular-nums tracking-titulo">{formatBRL(nf.valor)}</div>
      </div>
    }
  />
);

/** Duas colunas (principal 2:1 lateral) que quebram em telas estreitas. */
export const LayoutDetalheNF = ({ principal, lateral }: { principal: ReactNode; lateral: ReactNode }) => (
  <div className="flex flex-wrap items-start gap-4">
    <div className="flex min-w-0 flex-col gap-4" style={{ flex: "2 1 520px" }}>
      {principal}
    </div>
    <div className="flex min-w-0 flex-col gap-4" style={{ flex: "1 1 300px" }}>
      {lateral}
    </div>
  </div>
);

const Campo = ({ rotulo, children, largo }: { rotulo: string; children: ReactNode; largo?: boolean }) => (
  <div className={cn("flex flex-col gap-1", largo && "col-span-full")}>
    <span className="text-[11px] uppercase tracking-[0.06em] text-muted-foreground">{rotulo}</span>
    <span className={cn("text-sm", largo ? "whitespace-pre-line leading-relaxed" : "font-medium")}>{children}</span>
  </div>
);

/** Grade de campos (rótulo 11px uppercase sobre valor 14px) + descrição. */
export const CamposNF = ({ nf }: { nf: NotaFiscal }) => (
  <section
    className="grid gap-x-6 gap-y-[18px] rounded-xl border bg-card px-6 py-5"
    style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}
  >
    <Campo rotulo="Tomador">{nf.tomadorNome ?? "—"}</Campo>
    <Campo rotulo="Mês de referência">
      <span className="capitalize">{formatMesRef(nf.mesReferencia)}</span>
    </Campo>
    <Campo rotulo="Departamento">{nf.departamento || "—"}</Campo>
    <Campo rotulo="Enviada em">
      <span className="tabular-nums">{formatDateTime(nf.dataEnvio)}</span>
    </Campo>
    <Campo rotulo="Valor">
      <span className="tabular-nums">{formatBRL(nf.valor)}</span>
    </Campo>
    {nf.analisadoPor && nf.dataAnalise && (
      <Campo rotulo="Analisada por">
        {nf.analisadoPor} <span className="text-muted-foreground tabular-nums">· {formatDateTime(nf.dataAnalise)}</span>
      </Campo>
    )}
    <Campo rotulo="Descrição do serviço" largo>
      {nf.descricao || "—"}
    </Campo>
  </section>
);

/** Card do PDF com Visualizar (modal com o documento) e Baixar. */
export const ArquivoNF = ({ nf }: { nf: NotaFiscal }) => {
  const [baixando, setBaixando] = useState(false);
  const [viewerAberto, setViewerAberto] = useState(false);
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [viewerCarregando, setViewerCarregando] = useState(false);

  const baixar = async () => {
    setBaixando(true);
    try {
      const { blob, filename } = await api.getFile(`/nfs/${nf.id}/arquivo`);
      triggerDownload(blob, filename);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao baixar arquivo");
    } finally {
      setBaixando(false);
    }
  };

  const visualizar = async () => {
    setViewerCarregando(true);
    setViewerAberto(true);
    try {
      const { url } = await api.get<{ url: string }>(`/nfs/${nf.id}/visualizar`);
      setViewerUrl(url);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao obter link de visualização");
      setViewerAberto(false);
    } finally {
      setViewerCarregando(false);
    }
  };

  return (
    <>
      <section className="flex flex-wrap items-center gap-2.5 rounded-xl border bg-card px-4 py-3">
        <FileText className="h-4 w-4 shrink-0 text-primary" />
        <span className="min-w-0 flex-1 truncate text-sm font-medium" title={nf.arquivoNome}>
          {nf.arquivoNome}
        </span>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-[30px] px-2.5 text-[13px]" onClick={visualizar} disabled={viewerCarregando}>
            <Eye />
            {viewerCarregando ? "Carregando..." : "Visualizar"}
          </Button>
          <Button variant="ghost" size="sm" className="h-[30px] px-2.5 text-[13px]" onClick={baixar} disabled={baixando}>
            <Download />
            {baixando ? "Baixando..." : "Baixar"}
          </Button>
        </div>
      </section>

      <Dialog
        open={viewerAberto}
        onOpenChange={(open) => {
          setViewerAberto(open);
          if (!open) setViewerUrl(null);
        }}
      >
        <DialogContent className="flex h-[90vh] max-w-4xl flex-col gap-0 p-0">
          <DialogHeader className="shrink-0 border-b px-4 py-3">
            <DialogTitle>Visualizar NF nº {nf.numero}</DialogTitle>
            <DialogDescription>{nf.arquivoNome}</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-hidden">
            {viewerCarregando || !viewerUrl ? (
              <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Carregando documento...
              </div>
            ) : (
              <iframe src={viewerUrl} className="h-full w-full border-0" title={`NF ${nf.numero}`} />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

/** Card vermelho com o motivo da recusa (e quem recusou, quando houver). */
export const MotivoRecusaNF = ({ nf }: { nf: NotaFiscal }) => (
  <section className="flex gap-3 rounded-xl border border-destructive/35 bg-destructive/5 px-[18px] py-4">
    <AlertOctagon className="mt-px h-[18px] w-[18px] shrink-0 text-destructive" />
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-sm font-semibold text-destructive">Motivo da recusa</span>
      <p className="whitespace-pre-line text-sm leading-relaxed">{nf.motivoRecusa}</p>
      {nf.analisadoPor && nf.dataAnalise && (
        <p className="text-xs text-muted-foreground">
          Por {nf.analisadoPor} em {formatDateTime(nf.dataAnalise)}
        </p>
      )}
    </div>
  </section>
);

/** Cor do ponto da timeline pela ação gravada no histórico (o OCR grava o status final). */
function corDoEvento(acao: string): string {
  if (acao === "Aprovada") return "bg-success";
  if (acao === "Recusada" || acao === "Cancelada") return "bg-destructive";
  if (["Com ressalvas", "Erro OCR", "Pendente", "CNPJ Inválido", "A validar"].includes(acao)) return "bg-amber-500";
  if (acao === "Em análise") return "bg-primary";
  return "bg-muted-foreground/50";
}

/** Histórico em timeline (ponto colorido + linha vertical), mais recente no topo. */
export const HistoricoNF = ({ nf }: { nf: NotaFiscal }) => {
  // O backend devolve em ordem cronológica.
  const eventos = [...nf.historico].reverse();
  return (
    <section className="rounded-xl border bg-card p-[18px]">
      <h3 className="mb-3.5 text-[15px] font-semibold">Histórico</h3>
      {eventos.length === 0 ? (
        <p className="text-sm text-muted-foreground">Sem eventos.</p>
      ) : (
        <ol className="flex flex-col">
          {eventos.map((h, i) => (
            <li key={i} className="grid grid-cols-[14px_1fr] gap-3">
              <div className="flex flex-col items-center">
                <span className={cn("mt-1 h-2.5 w-2.5 rounded-full", corDoEvento(h.action))} />
                {i < eventos.length - 1 && <span className="my-1 w-0.5 flex-1 bg-border-soft" />}
              </div>
              <div className="flex min-w-0 flex-col gap-0.5 pb-4">
                <span className="text-sm font-medium">{h.action}</span>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {formatDateTime(h.date)} · {h.by}
                </span>
                {h.note && <span className="mt-0.5 whitespace-pre-line text-xs italic text-muted-foreground">"{h.note}"</span>}
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
};

/** Estados de carregando / não encontrada do detalhe. */
export const EstadoDetalheNF = ({ carregando, children }: { carregando: boolean; children: ReactNode }) => (
  <section className="flex items-center justify-center gap-2 rounded-xl border bg-card py-12 text-sm text-muted-foreground">
    {carregando ? (
      <>
        <Loader2 className="h-5 w-5 animate-spin" /> Carregando...
      </>
    ) : (
      children
    )}
  </section>
);
